import React, { useState, useEffect, useRef } from 'react';
import { Heart, Calendar, MapPin, Clock, User, Download, Sparkles, Star, Zap, Activity, CheckCircle2, Droplets, Wand2, Loader2, ScrollText, RefreshCw, Mail, ShieldAlert, FileText, Copyright, BarChart3, Hourglass, Printer } from 'lucide-react';

// --- COMPONENTS DEFINITI FUORI PER EVITARE RENDER LOOP ---

const FluidInput = ({ label, value, onChange, type = "text", placeholder, icon: Icon, options }) => {
  const [isFocused, setIsFocused] = useState(false);
  const safeValue = value || ''; 
  const hasValue = String(safeValue).length > 0;
  
  const shouldFloat = isFocused || hasValue || type === 'date' || type === 'time' || options;

  return (
    <div className={`relative group w-full transition-all duration-300 ${isFocused ? 'scale-[1.01]' : 'scale-100'}`}>
      
      {/* Background Layer */}
      <div className={`
          absolute inset-0 rounded-xl border transition-all duration-300 pointer-events-none
          ${isFocused 
              ? 'bg-white border-indigo-300 shadow-md ring-4 ring-indigo-50/50' 
              : 'bg-gray-50/50 border-transparent hover:bg-white hover:border-gray-200'}
      `} />

      {/* Icon Layer */}
      <div className={`
          absolute left-4 top-1/2 -translate-y-1/2 transition-colors duration-300 z-10 pointer-events-none
          ${isFocused || hasValue ? 'text-indigo-500' : 'text-gray-400'}
      `}>
        {Icon && <Icon size={18} strokeWidth={2.5} />}
      </div>

      {/* Input Layer */}
      <div className="relative z-10">
          {options ? (
              <select
                  value={safeValue}
                  onChange={onChange}
                  onFocus={() => setIsFocused(true)}
                  onBlur={() => setIsFocused(false)}
                  className="w-full bg-transparent text-gray-800 font-bold pl-12 pr-4 h-[60px] pt-4 rounded-xl outline-none appearance-none cursor-pointer"
              >
                  {options.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
              </select>
          ) : (
              <input 
                  type={type} 
                  value={safeValue}
                  onChange={onChange}
                  onFocus={() => setIsFocused(true)}
                  onBlur={() => setIsFocused(false)}
                  className={`w-full bg-transparent text-gray-800 font-bold pl-12 pr-10 h-[60px] rounded-xl outline-none placeholder-transparent ${shouldFloat ? 'pt-6 pb-2' : 'py-4'}`}
                  placeholder={label}
                  autoComplete="off"
              />
          )}
      </div>

      {/* Floating Label Layer */}
      <label className={`
          absolute left-12 transition-all duration-200 pointer-events-none z-20 origin-left
          ${shouldFloat 
              ? 'top-2 text-[10px] font-bold uppercase tracking-wider text-indigo-500 transform translate-y-0' 
              : 'top-1/2 -translate-y-1/2 text-gray-500 text-sm font-medium'}
      `}>
          {label}
      </label>

      {/* Success Indicator */}
      <div className={`absolute right-4 top-1/2 -translate-y-1/2 transition-all duration-500 z-10 pointer-events-none ${hasValue ? 'opacity-100 scale-100 text-emerald-500' : 'opacity-0 scale-75'}`}>
          <CheckCircle2 size={16} strokeWidth={3} />
      </div>
    </div>
  );
};

const PartnerSection = ({ title, data, setData, themeColor }) => (
  <div className={`
      relative bg-white p-6 rounded-3xl transition-all duration-500
      border border-gray-100 hover:border-${themeColor}-200
      shadow-[0_10px_40px_-15px_rgba(0,0,0,0.05)] hover:shadow-[0_20px_50px_-15px_rgba(0,0,0,0.1)]
  `}>
      <div className="flex items-center gap-3 mb-6 pl-1">
          <div className={`p-2 rounded-lg bg-${themeColor}-50 text-${themeColor}-500`}>
              <User size={20} strokeWidth={2.5} />
          </div>
          <h3 className="font-bold text-gray-800 text-lg tracking-tight">{title}</h3>
          <div className="ml-auto flex gap-1">
              {['name', 'gender', 'dob', 'pob'].map((field, i) => (
                  <div key={i} className={`h-1.5 w-6 rounded-full transition-colors duration-500 ${data[field] ? `bg-${themeColor}-400` : 'bg-gray-100'}`} />
              ))}
          </div>
      </div>

      <div className="space-y-5">
          <FluidInput 
              label="Nome Completo" 
              value={data.name} 
              onChange={(e) => setData({...data, name: e.target.value})}
              icon={User}
          />
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
              <div className="sm:col-span-1">
                   <FluidInput 
                      label="Genere"
                      value={data.gender}
                      onChange={(e) => setData({...data, gender: e.target.value})}
                      options={[
                          {value: 'female', label: 'Donna'},
                          {value: 'male', label: 'Uomo'},
                          {value: 'other', label: 'Altro'}
                      ]}
                      icon={Droplets}
                  />
              </div>
              <div className="sm:col-span-2">
                  <FluidInput 
                      label="Data di Nascita"
                      type="date"
                      value={data.dob}
                      onChange={(e) => setData({...data, dob: e.target.value})}
                      icon={Calendar}
                  />
              </div>
          </div>
          <div className="grid grid-cols-5 gap-5">
               <div className="col-span-3">
                  <FluidInput 
                      label="Luogo di Nascita"
                      value={data.pob}
                      onChange={(e) => setData({...data, pob: e.target.value})}
                      icon={MapPin}
                  />
               </div>
               <div className="col-span-2">
                  <FluidInput 
                      label="Ora"
                      type="time"
                      value={data.tob}
                      onChange={(e) => setData({...data, tob: e.target.value})}
                      icon={Clock}
                  />
               </div>
          </div>
      </div>
  </div>
);

// --- Biorhythm Bar Component ---
const BiorhythmBar = ({ label, valA, valB, color }) => {
    const diff = Math.abs(valA - valB);
    const sync = Math.max(0, 100 - diff);
    
    return (
        <div className="mb-4">
            <div className="flex justify-between text-xs font-bold uppercase tracking-wider text-gray-500 mb-1">
                <span>{label}</span>
                <span className={`${sync > 80 ? 'text-green-500' : sync > 50 ? 'text-yellow-500' : 'text-orange-500'}`}>
                    Sincronia: {sync}%
                </span>
            </div>
            <div className="relative h-2 bg-gray-100 rounded-full overflow-hidden">
                <div className="absolute top-0 bottom-0 w-1 bg-pink-500 z-10" style={{ left: `${valA}%` }} title="Partner A"></div>
                <div className="absolute top-0 bottom-0 w-1 bg-indigo-500 z-10" style={{ left: `${valB}%` }} title="Partner B"></div>
                <div 
                    className={`absolute top-0 bottom-0 opacity-30 ${color}`}
                    style={{ left: `${Math.min(valA, valB)}%`, width: `${Math.abs(valA - valB)}%` }}
                ></div>
            </div>
        </div>
    );
};

// --- MEANINGS FOR COUPLE NUMBERS ---
const combinedPathMeanings = {
  1: "Unione dinamica: insieme siete pionieri pronti a guidare.",
  2: "Armonia pura: la vostra forza risiede nella collaborazione e nel supporto reciproco.",
  3: "Gioia creativa: una relazione caratterizzata da comunicazione brillante e socialità.",
  4: "Fondamenta solide: insieme costruite stabilità e sicurezza duratura.",
  5: "Libertà condivisa: una coppia avventurosa che ama il cambiamento e il movimento.",
  6: "Nido d'amore: la cura, la famiglia e l'armonia domestica sono il vostro centro.",
  7: "Sintonia intellettuale: un legame profondo basato sulla ricerca e la comprensione.",
  8: "Potere realizzativo: insieme avete la capacità di materializzare grandi progetti.",
  9: "Amore universale: una coppia ispirata da ideali elevati e compassione.",
  11: "Visione illuminata: un legame intuitivo che ispira gli altri.",
  22: "Grandi costruttori: avete il potenziale per realizzare sogni su vasta scala.",
  33: "Guida spirituale: un amore che cura e insegna attraverso l'esempio.",
  0: "Calcolo in corso..." 
};

// --- MAIN APP COMPONENT ---

const AffinityApp = () => {
  const [partnerA, setPartnerA] = useState({
    name: '',
    gender: 'female',
    dob: '',
    tob: '',
    pob: ''
  });
  const [partnerB, setPartnerB] = useState({
    name: '',
    gender: 'male',
    dob: '',
    tob: '',
    pob: ''
  });
  
  const [result, setResult] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [todayDate, setTodayDate] = useState('');
  
  const [aiInsight, setAiInsight] = useState('');
  const [isGeneratingAi, setIsGeneratingAi] = useState(false);
  const aiSectionRef = useRef(null);

  useEffect(() => {
      const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
      setTodayDate(new Date().toLocaleDateString('it-IT', options));
  }, []);

  const handleReset = () => {
    setPartnerA({ name: '', gender: 'female', dob: '', tob: '', pob: '' });
    setPartnerB({ name: '', gender: 'male', dob: '', tob: '', pob: '' });
    setResult(null);
    setAiInsight('');
    setIsGeneratingAi(false);
  };

  // Funzione avanzata che assegna anche un indice (0-11) per calcolare la distanza zodiacale
  const getZodiacSign = (dateString) => {
    if (!dateString) return { name: '...', element: '...', index: -1 };
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return { name: '...', element: '...', index: -1 };
    
    const day = date.getDate();
    const month = date.getMonth() + 1;

    if ((month === 3 && day >= 21) || (month === 4 && day <= 19)) return { name: 'Ariete', element: 'Fuoco', index: 0 };
    if ((month === 4 && day >= 20) || (month === 5 && day <= 20)) return { name: 'Toro', element: 'Terra', index: 1 };
    if ((month === 5 && day >= 21) || (month === 6 && day <= 20)) return { name: 'Gemelli', element: 'Aria', index: 2 };
    if ((month === 6 && day >= 21) || (month === 7 && day <= 22)) return { name: 'Cancro', element: 'Acqua', index: 3 };
    if ((month === 7 && day >= 23) || (month === 8 && day <= 22)) return { name: 'Leone', element: 'Fuoco', index: 4 };
    if ((month === 8 && day >= 23) || (month === 9 && day <= 22)) return { name: 'Vergine', element: 'Terra', index: 5 };
    if ((month === 9 && day >= 23) || (month === 10 && day <= 22)) return { name: 'Bilancia', element: 'Aria', index: 6 };
    if ((month === 10 && day >= 23) || (month === 11 && day <= 21)) return { name: 'Scorpione', element: 'Acqua', index: 7 };
    if ((month === 11 && day >= 22) || (month === 12 && day <= 21)) return { name: 'Sagittario', element: 'Fuoco', index: 8 };
    if ((month === 12 && day >= 22) || (month === 1 && day <= 19)) return { name: 'Capricorno', element: 'Terra', index: 9 };
    if ((month === 1 && day >= 20) || (month === 2 && day <= 18)) return { name: 'Acquario', element: 'Aria', index: 10 };
    if ((month === 2 && day >= 19) || (month === 3 && day <= 20)) return { name: 'Pesci', element: 'Acqua', index: 11 };
    
    return { name: 'Sconosciuto', element: 'Mistero', index: -1 };
  };

  const calculateLifePathNumber = (dateString) => {
    if (!dateString) return 0;
    const digits = dateString.replace(/-/g, '').split('').map(Number);
    if (digits.some(isNaN)) return 0;
    let sum = digits.reduce((a, b) => a + b, 0);
    while (sum > 9 && sum !== 11 && sum !== 22 && sum !== 33) {
      sum = String(sum).split('').map(Number).reduce((a, b) => a + b, 0);
    }
    return sum;
  };

  const calculateCombinedPath = (numA, numB) => {
      let sum = numA + numB;
      while (sum > 9 && sum !== 11 && sum !== 22 && sum !== 33) {
          sum = String(sum).split('').map(Number).reduce((a, b) => a + b, 0);
      }
      return sum;
  }

  const calculateAgeDiff = (d1, d2) => {
    let date1 = new Date(d1);
    let date2 = new Date(d2);
    if (date1 > date2) [date1, date2] = [date2, date1];

    let years = date2.getFullYear() - date1.getFullYear();
    let months = date2.getMonth() - date1.getMonth();
    let days = date2.getDate() - date1.getDate();

    if (days < 0) {
        months--;
        const prevMonth = new Date(date2.getFullYear(), date2.getMonth(), 0).getDate();
        days += prevMonth;
    }
    if (months < 0) {
        years--;
        months += 12;
    }
    return { years, months, days };
  };

  const calculateBiorhythm = (dobString) => {
      if (!dobString) return { physical: 50, emotional: 50, intellectual: 50 };
      const dob = new Date(dobString);
      const today = new Date();
      const diffTime = Math.abs(today - dob);
      const days = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      const getVal = (cycle) => Math.round((Math.sin((2 * Math.PI * days) / cycle) + 1) * 50);
      return { physical: getVal(23), emotional: getVal(28), intellectual: getVal(33) };
  };

  // --- MOTORE DI AFFINITÀ DI PRECISIONE ---
  const calculateAffinityScore = (signA, signB, numA, numB, bioA, bioB) => {
    if (signA.index === -1 || signB.index === -1) return 0;

    // 1. Astrologia: Calcolo esatto degli "Aspetti" (Distanza Zodiacale)
    let astroDistance = Math.abs(signA.index - signB.index);
    if (astroDistance > 6) astroDistance = 12 - astroDistance; // Percorso più breve sulla ruota
    
    // Punteggi basati sugli aspetti astrologici reali
    const aspectScores = {
        0: 85, // Congiunzione (Stesso segno - Forte intesa, a volte troppa similitudine)
        1: 55, // Semi-Sestile (1 segno di distanza - Attrito e incomprensioni)
        2: 85, // Sestile (2 segni di distanza - Elementi compatibili, ottima comunicazione)
        3: 50, // Quadratura (3 segni di distanza - Stessa modalità, sfide e tensione)
        4: 95, // Trigono (4 segni di distanza - Stesso elemento, armonia perfetta)
        5: 45, // Quinconce (5 segni di distanza - Totalmente diversi, serve molto lavoro)
        6: 75  // Opposizione (6 segni di distanza - Gli opposti si attraggono, grande passione)
    };
    const astroScore = aspectScores[astroDistance] || 50;

    // 2. Numerologia: Matrice esatta di Compatibilità Pitagorica
    // Riduciamo i numeri maestri alla singola cifra per la compatibilità di base
    const baseA = numA > 9 ? (numA === 11 ? 2 : (numA === 22 ? 4 : 6)) : numA;
    const baseB = numB > 9 ? (numB === 11 ? 2 : (numB === 22 ? 4 : 6)) : numB;
    
    const numerologyMatrix = {
        1: {1: 75, 2: 70, 3: 90, 4: 50, 5: 95, 6: 60, 7: 85, 8: 50, 9: 90},
        2: {1: 70, 2: 85, 3: 75, 4: 85, 5: 50, 6: 95, 7: 70, 8: 90, 9: 65},
        3: {1: 90, 2: 75, 3: 85, 4: 55, 5: 90, 6: 80, 7: 60, 8: 65, 9: 90},
        4: {1: 50, 2: 85, 3: 55, 4: 80, 5: 45, 6: 85, 7: 90, 8: 95, 9: 50},
        5: {1: 95, 2: 50, 3: 90, 4: 45, 5: 85, 6: 65, 7: 85, 8: 55, 9: 80},
        6: {1: 60, 2: 95, 3: 80, 4: 85, 5: 65, 6: 90, 7: 60, 8: 85, 9: 95},
        7: {1: 85, 2: 70, 3: 60, 4: 90, 5: 85, 6: 60, 7: 75, 8: 50, 9: 75},
        8: {1: 50, 2: 90, 3: 65, 4: 95, 5: 55, 6: 85, 7: 50, 8: 80, 9: 60},
        9: {1: 90, 2: 65, 3: 90, 4: 50, 5: 80, 6: 95, 7: 75, 8: 60, 9: 85}
    };
    const numeroScore = numerologyMatrix[baseA]?.[baseB] || 50;

    // 3. Bioritmi: Sincronia energetica attuale (media delle 3 onde)
    const syncP = Math.max(0, 100 - Math.abs(bioA.physical - bioB.physical));
    const syncE = Math.max(0, 100 - Math.abs(bioA.emotional - bioB.emotional));
    const syncI = Math.max(0, 100 - Math.abs(bioA.intellectual - bioB.intellectual));
    const biorhythmScore = (syncP + syncE + syncI) / 3;

    // Calcolo Punteggio Finale Pesato
    // 45% Astrologia, 35% Numerologia, 20% Bioritmi di oggi
    const finalScore = Math.round((astroScore * 0.45) + (numeroScore * 0.35) + (biorhythmScore * 0.20));
    
    return Math.min(100, Math.max(10, finalScore));
  };

  useEffect(() => {
    setAiInsight('');
    
    if (partnerA.dob && partnerB.dob) {
      setIsCalculating(true);
      const timer = setTimeout(() => {
        const signA = getZodiacSign(partnerA.dob);
        const signB = getZodiacSign(partnerB.dob);
        const numA = calculateLifePathNumber(partnerA.dob);
        const numB = calculateLifePathNumber(partnerB.dob);
        const bioA = calculateBiorhythm(partnerA.dob);
        const bioB = calculateBiorhythm(partnerB.dob);
        const ageDiff = calculateAgeDiff(partnerA.dob, partnerB.dob);
        const combinedPath = calculateCombinedPath(numA, numB);
        
        // Passiamo tutto al nuovo motore preciso
        const score = calculateAffinityScore(signA, signB, numA, numB, bioA, bioB);

        let narrative = "";
        if (score >= 90) narrative = "Un legame celestiale perfetto. Siete l'uno il riflesso dell'altro.";
        else if (score >= 80) narrative = "Sinergia potente e duratura. Le stelle e i numeri vi sorridono.";
        else if (score >= 65) narrative = "Ottima intesa di base, con margini per crescere insieme.";
        else if (score >= 50) narrative = "Incontro dinamico. Siete diversi, ma complementari se ci lavorate.";
        else narrative = "Sfida evolutiva. Le incomprensioni sono lezioni celate dal destino.";

        setResult({
          signA, signB, numA, numB, bioA, bioB, score, narrative, ageDiff, combinedPath,
          timestamp: new Date().toLocaleDateString('it-IT')
        });
        setIsCalculating(false);
      }, 800); // Leggero delay extra per mostrare l'elaborazione "pesante"
      return () => clearTimeout(timer);
    } else {
      setResult(null);
    }
  }, [partnerA, partnerB]);

  const generateAiOracleReading = async () => {
    if (!result) return;
    setIsGeneratingAi(true);
    
    const todayFull = new Date().toLocaleDateString('it-IT', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
    const prompt = `
      Agisci come un esperto astrologo mistico. Oggi è ${todayFull}.
      Analizza la compatibilità tra:
      1. ${partnerA.name || 'Partner A'} (${result.signA.name}, Vita ${result.numA})
      2. ${partnerB.name || 'Partner B'} (${result.signB.name}, Vita ${result.numB})
      Punteggio Base: ${result.score}%.
      Numero di Coppia: ${result.combinedPath}.
      Crea una "Lettura Cosmica del Giorno" (max 150 parole).
      Struttura la risposta in tre brevi paragrafi:
      1. L'Alchimia di Base (la loro natura e il significato del numero di coppia).
      2. L'Energia di Oggi (come le stelle di oggi influenzano la coppia).
      3. Il Consiglio per Oggi (un'azione concreta per migliorare l'armonia oggi).
      Usa un tono mistico ma utile. Rispondi in Italiano.
    `;

    try {
      let response;
      try {
          response = await fetch('/.netlify/functions/gemini', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ prompt })
          });
      } catch (networkError) {
          throw new Error(`Errore di Rete (${networkError.message}).\n\nSe sei in locale usa 'netlify dev'. Se sei su affinitylove.net, disabilita l'AdBlocker o controlla la connessione.`);
      }
      
      let data;
      try {
          data = await response.json();
      } catch (parseError) {
          throw new Error("Il server Netlify non ha risposto con dati validi. Verifica che la cartella 'netlify/functions' sia stata caricata correttamente su GitHub.");
      }

      if (!response.ok) {
          throw new Error(data.error || `Errore del server API (${response.status})`);
      }
      
      const text = data.candidates?.[0]?.content?.parts?.[0]?.text;
      
      if (text) {
        setAiInsight(text);
        setTimeout(() => { aiSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }, 100);
      } else {
        throw new Error("Formato della risposta AI non valido.");
      }
    } catch (error) {
      console.error("AI Error Detailed:", error);
      setAiInsight(`⚠️ Diagnostica Errore:\n${error.message}`);
    } finally {
      setIsGeneratingAi(false);
    }
  };

  const handlePrint = () => { window.print(); };

  return (
    <div className="min-h-screen bg-pink-100 font-sans text-gray-800 flex flex-col print:bg-white overflow-x-hidden relative">
      
      <nav className="sticky top-0 z-30 px-6 py-4 bg-white/80 backdrop-blur-xl border-b border-gray-200/50 flex justify-between items-center print:hidden">
        <div className="flex items-center gap-3">
            <div className="bg-indigo-600 p-2 rounded-xl text-white shadow-lg shadow-indigo-200">
                <Sparkles size={18} strokeWidth={2.5} />
            </div>
            <div>
                <span className="font-bold text-xl tracking-tight text-gray-900 block leading-none">Affinity</span>
                <span className="text-[10px] text-gray-400 font-medium uppercase tracking-widest">{todayDate}</span>
            </div>
        </div>
        
        <div className="flex items-center gap-2">
            <button 
                onClick={handleReset}
                className="p-3 rounded-xl text-gray-400 hover:text-red-500 hover:bg-red-50 transition-all duration-300"
                title="Resetta tutto"
            >
                <RefreshCw size={20} />
            </button>
            <button 
            onClick={handlePrint}
            disabled={!result}
            className={`
                group flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-bold transition-all duration-300
                ${result 
                    ? 'bg-gray-900 text-white hover:bg-black hover:shadow-xl hover:-translate-y-0.5 cursor-pointer shadow-md' 
                    : 'bg-gray-100 text-gray-400 cursor-not-allowed'}
            `}
            >
            <Printer size={16} className="group-hover:animate-bounce" /> 
            <span className="font-bold">Salva come PDF</span>
            </button>
        </div>
      </nav>

      <main className="flex-1 p-4 md:p-8 max-w-[1600px] mx-auto w-full grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
        
        {/* INPUT PANEL */}
        <div className="lg:col-span-5 space-y-6 print:hidden">
            <div className="flex items-center justify-between px-2 mb-4">
                <div className="flex items-center gap-2 text-gray-400">
                    <Activity size={16} />
                    <span className="text-xs font-bold uppercase tracking-widest">Input Dati</span>
                </div>
            </div>
            <PartnerSection title="Primo Partner" data={partnerA} setData={setPartnerA} themeColor="pink" />
            <div className="flex justify-center -my-4 relative z-0">
                <div className="h-12 w-[1px] bg-gradient-to-b from-gray-200 to-transparent"></div>
            </div>
            <PartnerSection title="Secondo Partner" data={partnerB} setData={setPartnerB} themeColor="indigo" />
        </div>

        {/* RESULTS PANEL */}
        <div className="lg:col-span-7 w-full print:col-span-12 print:block sticky top-28 transition-all duration-700">
            {!result ? (
                <div className="h-full min-h-[600px] flex flex-col items-center justify-center rounded-[2.5rem] bg-white border border-gray-100 shadow-[0_20px_60px_-15px_rgba(0,0,0,0.05)] p-12 text-center relative overflow-hidden">
                    <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(99,102,241,0.05),transparent_70%)]"></div>
                    <div className="relative z-10">
                        <div className="w-24 h-24 bg-indigo-50 rounded-3xl rotate-3 flex items-center justify-center mb-8 mx-auto shadow-sm">
                            <Star size={40} className="text-indigo-400" strokeWidth={1.5} />
                        </div>
                        <h3 className="text-3xl font-bold text-gray-800 mb-3 tracking-tight">Esplora le Connessioni Cosmiche</h3>
                        <p className="text-gray-400 max-w-sm mx-auto leading-relaxed">
                            Attraverso l'antica saggezza e l'intelligenza artificiale moderna. Un viaggio digitale nell'affinità astrale.<br/><br/>
                            Compila i moduli a sinistra per iniziare.
                        </p>
                    </div>
                </div>
            ) : (
                <div className={`bg-white rounded-[2.5rem] shadow-[0_30px_80px_-20px_rgba(0,0,0,0.15)] print:shadow-none print:border print:border-gray-200 overflow-hidden transition-all duration-700 transform ${isCalculating ? 'scale-[0.99] opacity-90 blur-[1px]' : 'scale-100 opacity-100 blur-0'}`}>
                    
                    {/* Header Art */}
                    <div className="bg-[#0f172a] print:bg-white print:border-b-2 print:border-black text-white p-12 relative overflow-hidden">
                        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-indigo-500/20 rounded-full blur-[100px] transform translate-x-1/3 -translate-y-1/2 print:hidden"></div>
                        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-pink-500/10 rounded-full blur-[80px] transform -translate-x-1/3 translate-y-1/3 print:hidden"></div>
                        
                        <div className="relative z-10 flex flex-col md:flex-row justify-between items-end gap-8">
                            <div>
                                <h2 className="text-5xl font-serif mb-2 tracking-tight print:text-black">Affinità</h2>
                                <div className="flex items-center gap-3 text-indigo-200 print:text-gray-600">
                                    <span className="px-3 py-1 rounded-full border border-indigo-500/30 text-xs font-bold uppercase tracking-wider bg-indigo-500/10 print:bg-gray-100 print:border-gray-300">
                                        Report Ufficiale
                                    </span>
                                    <span className="text-sm opacity-60">{todayDate}</span>
                                </div>
                            </div>
                            <div className="flex items-end gap-2 print:text-black">
                                <span className="text-7xl font-bold tracking-tighter leading-none">{result.score}</span>
                                <span className="text-2xl text-indigo-400 print:text-gray-600 mb-2">%</span>
                            </div>
                        </div>
                    </div>

                    <div className="p-12">
                         {/* Static Narrative */}
                         <div className="mb-14 border-l-4 border-indigo-500 pl-6 py-2">
                             <p className="text-2xl text-gray-700 font-light leading-relaxed font-serif italic">
                                "{result.narrative}"
                             </p>
                         </div>

                         {/* Comparison Grid */}
                         <div className="grid grid-cols-2 gap-12 relative mb-12">
                            <div className="absolute left-1/2 top-0 bottom-0 w-[1px] bg-gray-100 -translate-x-1/2 hidden md:block"></div>
                            
                            {/* Partner A */}
                            <div className="text-center md:text-left space-y-4">
                                <div className="flex flex-col md:flex-row items-center md:justify-start gap-4 mb-6">
                                    <div className="w-12 h-12 rounded-full bg-pink-100 flex items-center justify-center text-pink-600 font-bold text-xl">
                                        {partnerA.name.charAt(0) || 'A'}
                                    </div>
                                    <div>
                                        <h4 className="font-bold text-xl text-gray-900">{partnerA.name || 'Partner A'}</h4>
                                        <p className="text-gray-400 text-sm">{partnerA.pob}</p>
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="bg-gray-50 print:border print:border-gray-200 p-4 rounded-2xl">
                                        <div className="text-xs text-gray-400 uppercase tracking-wider mb-1">Segno</div>
                                        <div className="font-bold text-gray-800">{result.signA.name}</div>
                                        <div className="text-xs text-pink-500">{result.signA.element}</div>
                                    </div>
                                    <div className="bg-gray-50 print:border print:border-gray-200 p-4 rounded-2xl">
                                        <div className="text-xs text-gray-400 uppercase tracking-wider mb-1">Numero Vita</div>
                                        <div className="font-mono font-bold text-gray-800 text-lg">#{result.numA}</div>
                                    </div>
                                </div>
                            </div>
                            
                            {/* Partner B */}
                            <div className="text-center md:text-right space-y-4">
                                <div className="flex flex-col md:flex-row-reverse items-center md:justify-start gap-4 mb-6">
                                    <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 font-bold text-xl">
                                        {partnerB.name.charAt(0) || 'B'}
                                    </div>
                                    <div>
                                        <h4 className="font-bold text-xl text-gray-900">{partnerB.name || 'Partner B'}</h4>
                                        <p className="text-gray-400 text-sm">{partnerB.pob}</p>
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="bg-gray-50 print:border print:border-gray-200 p-4 rounded-2xl">
                                        <div className="text-xs text-gray-400 uppercase tracking-wider mb-1">Numero Vita</div>
                                        <div className="font-mono font-bold text-gray-800 text-lg">#{result.numB}</div>
                                    </div>
                                    <div className="bg-gray-50 print:border print:border-gray-200 p-4 rounded-2xl">
                                        <div className="text-xs text-gray-400 uppercase tracking-wider mb-1">Segno</div>
                                        <div className="font-bold text-gray-800">{result.signB.name}</div>
                                        <div className="text-xs text-indigo-500">{result.signB.element}</div>
                                    </div>
                                </div>
                            </div>
                         </div>
                         
                         {/* DATE COMPARISON & NUMEROLOGY SECTION */}
                         <div className="mb-12 bg-indigo-50/50 print:bg-white print:border print:border-gray-200 rounded-3xl p-8 border border-indigo-100">
                             <div className="flex items-center gap-2 mb-6 text-gray-800">
                                <Hourglass size={20} className="text-indigo-600 print:text-gray-600" />
                                <h4 className="font-bold uppercase tracking-widest text-sm">Confronto Temporale & Numerologia</h4>
                             </div>
                             
                             <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                <div className="bg-white p-5 rounded-2xl shadow-sm print:shadow-none print:border print:border-gray-100">
                                    <div className="text-xs text-gray-400 uppercase font-bold mb-2">Differenza d'Età</div>
                                    <div className="flex items-baseline gap-2">
                                        <span className="text-2xl font-bold text-gray-800">{result.ageDiff.years}</span>
                                        <span className="text-sm text-gray-500">anni,</span>
                                        <span className="text-2xl font-bold text-gray-800">{result.ageDiff.months}</span>
                                        <span className="text-sm text-gray-500">mesi,</span>
                                        <span className="text-2xl font-bold text-gray-800">{result.ageDiff.days}</span>
                                        <span className="text-sm text-gray-500">giorni</span>
                                    </div>
                                    <div className="mt-2 text-xs text-gray-400">
                                        Tra {new Date(partnerA.dob).toLocaleDateString()} e {new Date(partnerB.dob).toLocaleDateString()}
                                    </div>
                                </div>

                                <div className="bg-white p-5 rounded-2xl shadow-sm border-l-4 border-indigo-400 print:shadow-none print:border-l-4 print:border-gray-300">
                                    <div className="flex justify-between items-start mb-2">
                                        <div className="text-xs text-gray-400 uppercase font-bold">Numero della Coppia</div>
                                        <div className="bg-indigo-100 text-indigo-700 print:bg-gray-100 print:text-gray-800 px-2 py-0.5 rounded text-xs font-bold font-mono">#{result.combinedPath}</div>
                                    </div>
                                    <p className="text-sm text-gray-600 italic leading-relaxed">
                                        "{combinedPathMeanings[result.combinedPath] || combinedPathMeanings[0]}"
                                    </p>
                                </div>
                             </div>
                         </div>

                         {/* BIORHYTHMS SECTION */}
                         <div className="mb-12 bg-gray-50 rounded-3xl p-8 border border-gray-100">
                             <div className="flex items-center gap-2 mb-6 text-gray-800">
                                <BarChart3 size={20} className="text-indigo-600 print:text-gray-600" />
                                <h4 className="font-bold uppercase tracking-widest text-sm">Energie di Oggi</h4>
                             </div>
                             
                             <BiorhythmBar label="Fisico (Energia & Salute)" valA={result.bioA.physical} valB={result.bioB.physical} color="bg-red-500" />
                             <BiorhythmBar label="Emotivo (Umore & Sensibilità)" valA={result.bioA.emotional} valB={result.bioB.emotional} color="bg-blue-500" />
                             <BiorhythmBar label="Intellettuale (Logica & Focus)" valA={result.bioA.intellectual} valB={result.bioB.intellectual} color="bg-green-500" />
                             
                             <p className="text-[10px] text-gray-400 mt-4 text-center">
                                *Sincronia calcolata sulla data odierna ({todayDate}). Le barre indicano i livelli energetici individuali.
                             </p>
                         </div>

                         {/* AI ORACLE SECTION */}
                         <div ref={aiSectionRef} className="mt-8 border-t border-gray-100 pt-8 print:hidden">
                            {!aiInsight ? (
                                <button 
                                    onClick={generateAiOracleReading}
                                    disabled={isGeneratingAi}
                                    className="w-full relative group overflow-hidden bg-gradient-to-r from-violet-600 to-indigo-600 text-white p-6 rounded-2xl shadow-xl transition-all duration-300 hover:shadow-2xl hover:scale-[1.01]"
                                >
                                    <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/stardust.png')] opacity-20 animate-pulse"></div>
                                    <div className="relative z-10 flex items-center justify-center gap-3">
                                        {isGeneratingAi ? (
                                            <>
                                                <Loader2 className="animate-spin" size={24} />
                                                <span className="font-serif text-lg">Consultando le stelle per oggi...</span>
                                            </>
                                        ) : (
                                            <>
                                                <Wand2 className="group-hover:rotate-12 transition-transform duration-300" size={24} />
                                                <span className="font-serif text-xl font-medium">Sblocca Lettura del Giorno AI ✨</span>
                                            </>
                                        )}
                                    </div>
                                </button>
                            ) : (
                                <div className="bg-gradient-to-br from-violet-50 to-fuchsia-50 rounded-3xl p-8 border border-violet-100 relative">
                                    <div className="flex items-center gap-2 mb-6 text-violet-800">
                                        <Sparkles size={20} className="text-fuchsia-500" />
                                        <h4 className="font-bold uppercase tracking-widest text-sm">Oroscopo del Giorno</h4>
                                    </div>
                                    <div className="prose prose-indigo prose-lg max-w-none font-serif text-gray-700 leading-relaxed">
                                        <p className="whitespace-pre-line">{aiInsight}</p>
                                    </div>
                                    <div className="flex justify-end mt-4">
                                        <button 
                                            onClick={generateAiOracleReading} 
                                            className="text-xs text-violet-400 hover:text-violet-600 flex items-center gap-1 transition-colors"
                                        >
                                            <Wand2 size={12} /> Nuova lettura
                                        </button>
                                    </div>
                                </div>
                            )}
                         </div>

                         {/* AI Print Version */}
                         {aiInsight && (
                             <div className="hidden print:block mt-8 border-t border-gray-200 pt-8">
                                <h4 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                                    <Sparkles size={16} /> Lettura Cosmica del Giorno
                                </h4>
                                <p className="text-gray-700 font-serif whitespace-pre-line text-sm leading-relaxed">
                                    {aiInsight}
                                </p>
                             </div>
                         )}

                    </div>
                    
                    {/* Report Footer (Prints Only) */}
                    <div className="hidden print:block bg-gray-50 p-8 border-t border-gray-200 text-center">
                        <div className="flex justify-center items-center gap-2 mb-2 text-gray-800 font-bold">
                            <Sparkles size={16} className="text-gray-600"/> Affinity
                        </div>
                        <p className="text-[10px] text-gray-500 leading-relaxed max-w-lg mx-auto mb-4">
                            Questo report è generato automaticamente a scopo di intrattenimento. Non costituisce consulenza professionale.
                        </p>
                         <div className="text-xs text-gray-400 font-medium">
                             &copy; 2026 @graffiti_anno_2060. Tutti i diritti riservati.
                         </div>
                    </div>
                </div>
            )}
        </div>

      </main>
      
      {/* GLOBAL FOOTER (Screen Only) */}
      <footer className="mt-12 bg-white border-t border-gray-200 py-12 px-6 print:hidden">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-12 text-sm text-gray-600">
            {/* Column 1: Info & Contact */}
            <div className="space-y-4">
                <h5 className="font-bold text-gray-900 text-lg flex items-center gap-2">
                    <Sparkles size={20} className="text-indigo-600 fill-indigo-100"/> Affinity
                </h5>
                <p className="leading-relaxed text-gray-500">
                    Esplora le connessioni cosmiche attraverso l'antica saggezza e l'intelligenza artificiale moderna. Un viaggio digitale nell'affinità astrale.
                </p>
                <div className="pt-2 space-y-2">
                    <div className="font-bold text-gray-900">@graffiti_anno_2060</div>
                    <a href="mailto:vcard@luminaessenza.it" className="text-indigo-600 hover:text-indigo-800 hover:underline flex items-center gap-2 transition-colors">
                        <Mail size={16} /> vcard@luminaessenza.it
                    </a>
                    <div className="flex items-center gap-1 text-xs text-gray-400 pt-2">
                        <Copyright size={12} /> 2026 Tutti i diritti riservati
                    </div>
                </div>
            </div>

            {/* Column 2: Disclaimer */}
            <div className="space-y-4">
                <h5 className="font-bold text-gray-900 text-lg flex items-center gap-2">
                    <ShieldAlert size={20} className="text-orange-500"/> Disclaimer Legale
                </h5>
                <div className="text-xs leading-relaxed text-gray-500 space-y-2 border-l-2 border-orange-100 pl-4">
                    <p>
                        "Affinity" è un'applicazione realizzata esclusivamente per scopi ludici, educativi e di intrattenimento.
                    </p>
                    <p>
                        Le analisi astrologiche, numerologiche e le letture generate dall'AI sono simulazioni basate su algoritmi e non possiedono validità scientifica comprovata.
                    </p>
                    <p>
                        Queste informazioni non costituiscono e non devono sostituire in alcun modo consulenze professionali in ambito psicologico, legale, medico o finanziario. L'autore declina ogni responsabilità per l'utilizzo delle informazioni fornite.
                    </p>
                </div>
            </div>

            {/* Column 3: Privacy */}
            <div className="space-y-4">
                <h5 className="font-bold text-gray-900 text-lg flex items-center gap-2">
                    <FileText size={20} className="text-green-600"/> Privacy Policy
                </h5>
                <div className="text-xs leading-relaxed text-gray-500">
                    <p className="mb-3">
                        La tua privacy è la nostra priorità assoluta. L'architettura di Affinity è progettata per la massima sicurezza:
                    </p>
                    <ul className="space-y-2">
                        <li className="flex items-start gap-2">
                            <CheckCircle2 size={14} className="text-green-500 mt-0.5 shrink-0" />
                            <span><strong>Elaborazione Locale:</strong> I calcoli matematici avvengono direttamente nel tuo browser.</span>
                        </li>
                        <li className="flex items-start gap-2">
                            <CheckCircle2 size={14} className="text-green-500 mt-0.5 shrink-0" />
                            <span><strong>Nessun Database:</strong> Non salviamo i tuoi dati (nomi, date, luoghi) su nessun server persistente.</span>
                        </li>
                        <li className="flex items-start gap-2">
                            <CheckCircle2 size={14} className="text-green-500 mt-0.5 shrink-0" />
                            <span><strong>Effimero:</strong> Basta ricaricare la pagina o cliccare "Reset" per cancellare ogni traccia della sessione.</span>
                        </li>
                    </ul>
                </div>
            </div>
        </div>
      </footer>

      <style>{`
        ::-webkit-scrollbar { width: 6px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 10px; }
        @media print {
            body { background: white; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            nav, footer, .lg\\:col-span-5, button { display: none !important; }
            .lg\\:col-span-7 { 
                width: 100% !important; 
                margin: 0 !important; 
                position: static !important; 
                display: block !important;
            }
            .shadow-\\[0_30px_80px_-20px_rgba\\(0\\,0\\,0\\,0\\.15\\)\\] { box-shadow: none !important; border: 1px solid #ddd; }
            .bg-\\[\\#0f172a\\] { background-color: white !important; color: black !important; border-bottom: 2px solid black; }
            .text-indigo-200 { color: #666 !important; }
            .text-white { color: black !important; }
            .bg-gray-50 { background-color: white !important; border: 1px solid #eee; }
        }
      `}</style>
    </div>
  );
};

export default AffinityApp;
